import { CreditReportAccountOverview } from "types/landlord/reports";
import { Disclosure, Transition } from "@headlessui/react";

import { Section } from "views/applications-id-report/components/Section";
import { ChevronDown, DotsHorizontal } from "untitledui-js-base";
import { Check } from "untitledui-js-base";

import { Row } from "views/applications-id-report/components/Row";
import { PaymentHistory } from "views/applications-id-report/slices/CreditReportSlice/components/PaymentHistory";
import {
  paymentHistoryGridIndex,
  paymentHistoryYears
} from "views/applications-id-report/slices/CreditReportSlice/utils/paymentHistoryGrid";
import { cx, formatToCurrency } from "shared/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import { DataVendors } from "shared/types";

/** TransUnion account type code for rental accounts */
const RENTAL_ACCOUNT_TYPE_CODE = 29;
/** TransUnion account type code string for rental accounts */
const RENTAL_ACCOUNT_TYPE_CODE_STRING = "RA";
/** TransUnion creditor classification code for rental/leasing */
const RENTAL_CREDITOR_CLASSIFICATION_CODE = 9;

export type FocusedLatePayment = {
  accountIndex: number;
  historyIndex: number;
  nonce: number;
};

interface AccountsOverviewProps {
  account_overview?: CreditReportAccountOverview[];
  isNoHit: boolean;
  verificationId: number;
  vendor?: DataVendors;
  focusedLatePayment?: FocusedLatePayment | null;
}

const isRentalAccount = (account: CreditReportAccountOverview) =>
  account.is_rental ??
  (Number(account.account_type?.code) === RENTAL_ACCOUNT_TYPE_CODE ||
    account.account_type?.code === RENTAL_ACCOUNT_TYPE_CODE_STRING ||
    Number(account.creditor_classification?.code) ===
      RENTAL_CREDITOR_CLASSIFICATION_CODE);

/* Manner of payment (North American Standard account ratings):
0 - Too new to rate; approved but not used.
1 - Pays (or paid) within 30 days of payment due date or not over one payment past due.
2 - Pays (or paid) in more than 30 days from payment due date, but not more than 60 days, or not more than two payments past due.
3 - Pays (or paid) in more than 60 days from payment due date, but not more than 90 days, or not more than three payments past due.
4 - Pays (or paid) in more than 90 days from payment due date, but not more than 120 days, or four payments past due.
5 - Account is at least 120 days overdue but is not yet rated "9."
7 - Making regular payments under a consolidation order or similar arrangement.
8 - Repossession (voluntary or involuntary return of merchandise).
9 - Bad debt; placed for collection; skip. */
const legend = [
  {
    children: <Check size="12px" className="stroke-success-700" />,
    className: "bg-success-50",
    description: "Pays account as agreed"
  },
  {
    children: null,
    description: "No payment history",
    className: "bg-alpha-80"
  },
  {
    children: null,
    description: "No rate reported/available",
    className: "bg-alpha-40"
  },
  {
    children: <DotsHorizontal size="12px" className="stroke-warning-600" />,
    description: "Too new to rate",
    className: "bg-warning-50"
  },
  {
    children: 2,
    description: "Not more than two payments past due",
    className: "bg-danger-50 text-danger-500"
  },
  {
    children: 3,
    description: "Not more than three payments past due",
    className: "bg-danger-50 text-danger-500"
  },
  {
    children: 4,
    description: "Four payments past due",
    className: "bg-danger-50 text-danger-500"
  },
  {
    children: 5,
    description: "At least 120 days or more than four payments past due",
    className: "bg-danger-50 text-danger-500"
  },
  {
    children: 6,
    className: "bg-danger-50 text-danger-500",

    description: "Collection account"
  },
  {
    children: 7,
    description: "Included in Chapter 13",
    className: "bg-danger-50 text-danger-500"
  },
  {
    children: 8,
    description: "Repossession",
    className: "bg-danger-50 text-danger-500"
  },
  {
    children: 9,
    description: "Charge-off",
    className: "bg-danger-50 text-danger-500"
  }
];

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

export const AccountsOverview = ({
  account_overview,
  isNoHit,
  verificationId,
  vendor,
  focusedLatePayment
}: AccountsOverviewProps) => {
  const { printVerificationsState, setPrintingConditions } =
    useContext(PrintAllContext);
  const [isOpen, setIsOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusedLatePayment) return;

    const container = headerRef.current?.parentElement;
    const accountButton = container?.querySelector<HTMLButtonElement>(
      `[data-account-index="${focusedLatePayment.accountIndex}"]`
    );

    if (accountButton?.getAttribute("aria-expanded") !== "true")
      accountButton?.click();

    const timeout = window.setTimeout(() => {
      const target =
        container?.querySelector('[data-late-highlight="true"]') ??
        accountButton;

      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [focusedLatePayment]);

  useEffect(() => {
    if (String(verificationId) in printVerificationsState)
      flushSync(() => {
        setIsOpen(true);
        setPrintingConditions((prev) => ({
          ...prev,
          [verificationId]: true
        }));
      });
  }, [printVerificationsState, verificationId]);

  if (isNoHit)
    return (
      <Section sectionTitle="Accounts overview" maxWidth="none">
        <p className="text-base font-medium text-content-300">
          No data available
        </p>
      </Section>
    );

  return (
    <Section sectionTitle="Accounts overview" maxWidth="none">
      <div
        ref={headerRef}
        className="mr-14 flex justify-between gap-2 border-b border-alpha-40 py-4"
      >
        <p className="text-sm font-medium text-content-300">Open Accounts</p>
        <div className="flex gap-2 lg:gap-14">
          <p className="text-sm font-medium text-content-300 w-[125px]">
            Balance
          </p>
          <p className="text-sm font-medium text-content-300 w-[80px]">
            Past due
          </p>
        </div>
      </div>

      {account_overview?.map((account, idx) => {
        const isChargeOff = [
          account.rate?.code,
          Number(account.account_rating)
        ].includes(9);
        const isFocused = focusedLatePayment?.accountIndex === idx;
        const highlightedIndex =
          focusedLatePayment && isFocused
            ? paymentHistoryGridIndex(
                account.payment_history,
                account.date_reported,
                focusedLatePayment.historyIndex
              )
            : null;

        return (
          <Disclosure key={`${account.account_number}_${idx}_${isOpen}`}>
            {({ open }) => (
              <>
                <Disclosure.Button
                  data-account-index={idx}
                  className="group flex w-full justify-between gap-2 border-b border-alpha-40 py-4 hover:bg-alpha-20 items-center"
                >
                  <p className="text-sm font-medium text-content-1000 truncate">
                    {account.account_name || ""}
                  </p>
                  <Show if={isRentalAccount(account)}>
                    <Badge variant={BadgeVariant.WARNING} size={BadgeSize.SM}>
                      Rental
                    </Badge>
                  </Show>
                  <div className="group flex items-center">
                    <div className="group flex items-center gap-2 lg:gap-14 mr-4 text-start">
                      {account.narrative_codes &&
                        account.narrative_codes.filter(
                          (el) => el.description === "COLLECTION ACCOUNT"
                        ).length > 0 && (
                          <Badge
                            variant={BadgeVariant.ERROR}
                            size={BadgeSize.SM}
                            className="max-w-[165px] truncate !block"
                          >
                            COLLECTION ACCOUNT
                          </Badge>
                        )}
                      <Show
                        if={
                          isChargeOff &&
                          vendor &&
                          [
                            DataVendors.TransUnion,
                            DataVendors.Equifax
                          ].includes(vendor)
                        }
                      >
                        <Badge
                          variant={BadgeVariant.ERROR}
                          size={BadgeSize.SM}
                          className="max-w-[165px] truncate !block"
                        >
                          Charge-off
                        </Badge>
                      </Show>
                      <Show
                        if={
                          account.narrative_codes &&
                          account.narrative_codes.some((el) => el.code === "DJ")
                        }
                      >
                        <Badge
                          variant={BadgeVariant.ERROR}
                          size={BadgeSize.SM}
                          className="max-w-[165px] truncate !block"
                        >
                          FORECLOSURE
                        </Badge>
                      </Show>
                      <p className="w-[125px]">
                        {account.balance
                          ? formatToCurrency(account.balance)
                          : "$0.00"}
                      </p>
                      <p className="w-[80px]">
                        {account.past_due_amount
                          ? formatToCurrency(account.past_due_amount)
                          : "$0.00"}
                      </p>
                    </div>
                    <IconButton
                      Icon={ChevronDown}
                      size={IconButtonSize.SM}
                      className={cx("transition-all", open && "rotate-180")}
                      borderLess
                    />
                  </div>
                </Disclosure.Button>
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <Disclosure.Panel className="border-b border-alpha-40 py-4">
                    <p className="mb-4 text-xl font-bold text-content-1000">
                      {account.account_name || "--"}
                    </p>

                    <div className="mb-6 flex w-full flex-wrap justify-between gap-x-3">
                      <div className="flex w-[360px] flex-col gap-3">
                        <Row
                          title="Account Type"
                          value={account.portfolio_type?.description}
                        />
                        <Row
                          title="Account Owner"
                          value={account.account_owner?.description}
                        />
                        <Row
                          title="Rate/Status"
                          value={
                            account.rate?.description ??
                            (isChargeOff ? "Charge-off" : null)
                          }
                        />
                        <Row title="Date Opened" value={account.date_opened} />
                        <Row
                          title="Date Reported"
                          value={account.date_reported}
                        />
                        <Row
                          title="Last Payment Date"
                          value={account.last_payment_date}
                        />
                        <Row
                          title="Charge Off Amount"
                          value={
                            isChargeOff && vendor === DataVendors.TransUnion
                              ? account.past_due_amount &&
                                formatToCurrency(account.past_due_amount)
                              : account.charge_off_amount &&
                                formatToCurrency(account.charge_off_amount)
                          }
                        />
                        <Row
                          title="Deferred Payment Start"
                          value={account.deferred_payment_start}
                        />
                        <Row
                          title="Terms Frequency"
                          value={account.terms_frequency?.description}
                        />
                        <Row
                          title="Terms Duration"
                          value={account.terms_duration?.description}
                        />
                        <Row
                          title="Previous High Rate 1"
                          value={account.previous_high_rate_one}
                        />
                        <Row
                          title="Previous High Date 1"
                          value={account.previous_high_date_one}
                        />
                        <Row
                          title="Narrative Codes"
                          value={account.narrative_codes
                            ?.map((el) => el.description)
                            .join(", ")}
                        />
                        <Row
                          title="Months Reviewed"
                          value={account.months_reviewed}
                        />
                        <Row title="Date Closed" value={account.date_closed} />
                      </div>

                      <div className="flex w-[360px] flex-col gap-3">
                        <Row
                          title="Balance"
                          value={
                            account.balance && formatToCurrency(account.balance)
                          }
                        />
                        <Row
                          title="Credit Limit"
                          value={
                            account.credit_limit &&
                            formatToCurrency(account.credit_limit)
                          }
                        />
                        <Row
                          title="High Credit"
                          value={
                            account.high_credit &&
                            formatToCurrency(account.high_credit)
                          }
                        />
                        <Row
                          title="Scheduled Payment"
                          value={
                            account.scheduled_payment_amount &&
                            formatToCurrency(account.scheduled_payment_amount)
                          }
                        />
                        <Row
                          title="Actual Payment"
                          value={
                            account.actual_payment_amount &&
                            formatToCurrency(account.actual_payment_amount)
                          }
                        />
                        <Row
                          title="Past Due"
                          value={
                            account.past_due_amount &&
                            formatToCurrency(account.past_due_amount)
                          }
                        />
                        <Row
                          title="Date Last Activity"
                          value={account.last_activity_date}
                        />
                        <Row
                          title="Date Major First Delinquency Reported"
                          value={account.date_major_first_delinquency_reported}
                        />
                        <Row
                          title="Creditor Classification"
                          value={account.creditor_classification?.description}
                        />
                        <Row
                          title="Original Creditor Name"
                          value={account.original_creditor_name}
                        />
                        <Row
                          title="Previous High Rate 2"
                          value={account.previous_high_rate_two}
                        />
                        <Row
                          title="Previous High Date 2"
                          value={account.previous_high_date_two}
                        />
                      </div>
                    </div>

                    {account.payment_history &&
                      account.payment_history.length > 0 && (
                        <>
                          <p className="font-bold text-content-1000">
                            Payment history
                          </p>

                          <div className="flex items-center gap-2">
                            <div className="grid grid-cols-1 gap-2 text-sm font-medium text-content-300">
                              <span className="h-10 w-10"></span>
                              {paymentHistoryYears(
                                account.payment_history,
                                account.date_reported
                              ).map((year, yearIdx) => (
                                <div
                                  className="flex h-10 w-10 shrink-0 items-center justify-center"
                                  key={`${year}_${yearIdx}`}
                                >
                                  <p>{year}</p>
                                </div>
                              ))}
                            </div>

                            <div className="grid max-w-[540px] grid-cols-12 gap-2">
                              {months.map((month) => {
                                return (
                                  <div
                                    className="flex h-10 w-10 items-center justify-center text-sm font-medium text-content-300"
                                    key={month}
                                  >
                                    <p>{month}</p>
                                  </div>
                                );
                              })}
                              <PaymentHistory
                                payment_history={account.payment_history}
                                date_reported={account.date_reported || ""}
                                highlightedIndex={highlightedIndex}
                              />
                            </div>
                          </div>

                          <div className="mt-4">
                            <p className="mb-4 text-sm font-medium text-content-300">
                              Legend
                            </p>
                            <div className="flex flex-wrap justify-between gap-x-2">
                              <div>
                                {legend.slice(0, 6).map((el, idx) => {
                                  return (
                                    <div
                                      key={idx}
                                      className="my-2 flex items-center gap-3 rounded-lg"
                                    >
                                      <div
                                        className={cx(
                                          el.className,
                                          "flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold"
                                        )}
                                      >
                                        {el.children}
                                      </div>
                                      <p className="text-xs font-medium text-content-1000">
                                        {el.description}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                              <div>
                                {legend
                                  .slice(6, legend.length)
                                  .map((el, idx) => {
                                    return (
                                      <div
                                        key={idx}
                                        className="my-2 flex items-center gap-3 rounded-lg"
                                      >
                                        <div
                                          className={cx(
                                            el.className,
                                            "flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold"
                                          )}
                                        >
                                          {el.children}
                                        </div>
                                        <p className="text-xs font-medium text-content-1000">
                                          {el.description}
                                        </p>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                  </Disclosure.Panel>
                </Transition>
              </>
            )}
          </Disclosure>
        );
      })}
    </Section>
  );
};
